import PlayArrowIcon from '@material-symbols/svg-300/outlined/play_arrow.svg'
import ShoppingCartOutlinedIcon from '@material-symbols/svg-300/outlined/shopping_cart.svg'
import classnames from 'classnames'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Markdown from 'react-markdown'
import { Link, useParams } from 'react-router-dom'
import Button from '../../components/basics/Button/Button'
import ThreadsDisplay from '../../components/comments/ThreadsDisplay/ThreadsDisplay'
import FavoriteBadge from '../../components/FavoriteBadge/FavoriteBadge'
import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import StartCourseLink from '../../components/StartCourseLink/StartCourseLink'
import { isLearningItemAsPath, LearningItem, LearningItemAsPath, OnDemandCourse } from '../../models/LearningItem'
import bookingForLearningItem from '../../state/calculations/bookingForLearningItem'
import {
  userCanBookLearningItem,
  userCanCommentOnRoot,
  userCanEditLearningItem,
  userCanPreviewLearningItem,
} from '../../state/calculations/permissions/userCan'
import { useLearningItem } from '../../state/entities'
import useBookOnDemandCourse from '../../state/mutations/useBookOnDemandCourse'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import styles from './CoursePage.module.scss'
import CoursePageAssets from './CoursePageAssets'
import {
  CoursePageScheduledCourseEvents,
  CoursePageScheduledCourseParticipants,
  CoursePageScheduledCourseQuickfacts,
} from './CoursePageScheduledCourse'
import OverlayChildPage from '../../components/OverlayChildPage/OverlayChildPage'
import { CourseLearningPathPage } from '../CourseLearningPathPage/CourseLearningPathPage'
import CourseActivity from './CourseActivity'

export default React.memo(function CoursePage() {
  const { uuid } = useParams()

  const { learningItem } = useLearningItem(uuid)

  let learningItemAsPath: LearningItemAsPath | null = null
  if (isLearningItemAsPath(learningItem)) {
    learningItemAsPath = learningItem
  }

  if (learningItemAsPath) {
    return <CourseLearningPathPage learningItemAsPath={learningItemAsPath} learningItem={learningItem} />
  }

  return <CoursePageForOnDemandOrScheduled learningItem={learningItem} />
})

/**********************************************************************
 * CoursePage
 **********************************************************************/
const CoursePageForOnDemandOrScheduled = React.memo(function CoursePage({
  learningItem,
}: {
  learningItem: LearningItem
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()
  const { uuid } = useParams()

  // book course
  const bookCourse = useBookOnDemandCourse()
  const handleBookCourse = useCallback(() => {
    learningItem.kind === 'onDemandCourse' && bookCourse.mutate({ courseUuid: learningItem.onDemandCourse.uuid })
  }, [learningItem, bookCourse])

  // existing booking
  const booking = bookingForLearningItem(learningItem)

  if (learningItem.error) {
    throw new Error('Not found')
  }

  return (
    <>
      <article className={classnames(styles.CoursePage, { skeleton: learningItem.isDummy })}>
        <header>
          <h1>{learningItem!.title}</h1>
        </header>

        <main>
          <section className={styles.courseDescription}>
            <LearningItemImage learningItem={learningItem} full className={styles.coverImage} />

            {learningItem.status !== 'published' && (
              <span className={styles.status}>
                <span>{t(`learning item.status.${learningItem.status}`)}</span>
              </span>
            )}

            <span className={styles.courseType}>{t(`learning item.type.${learningItem.type}`)}</span>

            <FavoriteBadge course={learningItem} className={styles.favoriteBadge} />

            <div className={styles.belowImage}>
              {learningItem.kind === 'onDemandCourse' && <OnDemandCourseQuickFacts course={learningItem} />}
              {learningItem.kind === 'scheduledCourse' && (
                <CoursePageScheduledCourseQuickfacts scheduledCourse={learningItem} />
              )}

              <div className={styles.actionsAndDescription}>
                <div className={styles.actions}>
                  {booking && <StartCourseLink filled learningItem={learningItem} />}

                  {!booking &&
                    userCanBookLearningItem(currentUser, learningItem) &&
                    learningItem.kind === 'onDemandCourse' && (
                      <>
                        {userCanPreviewLearningItem(currentUser, learningItem) && (
                          <Button component={Link} to={routes.previewCourse.url({ uuid: learningItem.uuid })}>
                            <PlayArrowIcon />
                            {t('learning item.preview')}
                          </Button>
                        )}
                        <Button filled onClick={handleBookCourse} loading={bookCourse.isPending}>
                          {t('learning item.book')}
                          <ShoppingCartOutlinedIcon />
                        </Button>
                      </>
                    )}

                  {userCanEditLearningItem(currentUser, learningItem) && (
                    <Button component={Link} to={routes.editCourse.url({ uuid: uuid!, kind: learningItem.kind })}>
                      {t('generic cta.edit')}
                    </Button>
                  )}

                  <div>
                    <CourseActivity learningItem={learningItem} />
                  </div>
                </div>

                <div className={styles.descriptionText}>
                  <Markdown>{learningItem.description}</Markdown>
                </div>
              </div>
            </div>
          </section>

          <CoursePageAssets learningItemUuid={learningItem.uuid} />

          {learningItem.kind === 'scheduledCourse' && (
            <CoursePageScheduledCourseEvents scheduledCourse={learningItem} />
          )}

          {userCanCommentOnRoot(currentUser, learningItem) && (
            <ThreadsDisplay learningItemUuid={learningItem.uuid} root={learningItem} />
          )}

          {learningItem.kind === 'scheduledCourse' && (
            <CoursePageScheduledCourseParticipants scheduledCourse={learningItem} />
          )}
        </main>

        <OverlayChildPage />
      </article>
    </>
  )
})

/**********************************************************************
 * OnDemandCourseQuickFacts
 **********************************************************************/
export const OnDemandCourseQuickFacts = React.memo(function OnDemandCourseQuickFacts({
  course,
}: {
  course: OnDemandCourse
}) {
  const { t } = useTranslation()

  return (
    <aside className={styles.quickfacts}>
      <h2>{t('learning item.quickFacts')}</h2>

      <dl>
        {course.categories.length > 0 && (
          <>
            <dt>{t('learning item.categories')}</dt>
            <dd>{course.categories.map((category) => category.name).join(', ')}</dd>
          </>
        )}

        <dt>{t('learning item.duration')}</dt>
        <dd>{t('format.{count} MINUTES', { count: course.onDemandCourse.durationInMin })}</dd>

        <dt>{t('learning item.contentLanguage')}</dt>
        <dd>
          {(course.onDemandCourse.contentLocales || ['n/a']).map((code) => t('format.LANGUAGE', { code })).join(', ')}
        </dd>

        {/*<dt>{t('audioLanguage')}</dt>*/}
        {/*<dd>{(course.course.audioLocales || ['n/a']).join(', ')}</dd>*/}

        {/*<dt>{t('subtitleLanguage')}</dt>*/}
        {/*<dd>{(course.course.subtitleLocales || ['n/a']).join(', ')}</dd>*/}

        <dt>{t('learning item.price')}</dt>
        <dd>{t('learning item.included')}</dd>
      </dl>
    </aside>
  )
})

export function CoursePageBreadcrumb() {
  const { uuid } = useParams()
  const { learningItem } = useLearningItem(uuid)

  return <span>{learningItem.title}</span>
}
